import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import { useCallback, useEffect, useMemo } from "react"
import { Grid } from "@mui/material"
import PropTypes from "prop-types"
import moment from "moment"
import { Loading } from "../Loading"
import { titleTypography } from "shared/styles/style"
import { descriptionTypography } from "shared/styles/style"
import InfoIcon, { TooltipIconColor, TooltipType } from "components/InfoIcon"
import { teacherInfoIconBlock } from "components/InfoIcon/teacherInfoIcon"
import makeStyles from "pages/style"
import {
  useMyCalendarAPIs,
  useLazyGetCountLegendLXOQuery,
  useLazyGetLXOCountTeachersQuery,
} from "shared/redux/endpoints/endPointMyCalendar"
import { ToastUtil } from "utilities/toastUtil"
import { useSelector } from "react-redux"
import { selectAuth } from "shared/redux/slices/authSlice"

import { RoleLabel } from "constant/Role"
import { Role } from "constant/Role"

const timeRangeCalendar = [
  {
    label: "Lesson",
    color: "#2576B9",
  },
  {
    label: "LESSON",
    color: "#2576B9",
  },
  {
    label: "PT demo",
    color: "#EABEFF",
  },
  {
    label: "Pt / Demo",
    color: "#EABEFF",
  },
  {
    label: "TASK",
    color: "#FFA500",
  },
  {
    label: "Task",
    color: "#FFA500",
  },
  {
    color: "#F4CE14",
    label: "Eocc & Test",
  },
  {
    color: "#F4CE14",
    label: "EOCC/TEST",
  },
  {
    label: "PT/DEMO",
    color: "#EABEFF",
  },
  {
    label: "Progress report",
    color: "#F19ED2",
  },
  {
    label: "Daily report",
    color: "springgreen",
  },
  {
    label: "Report",
    color: "springgreen",
  },
  {
    label: "Other Task",
    color: "orange",
  },
  {
    label: "Feedback",
    color: "#0DCAF0",
  },
  {
    label: "GradeBook report",
    color: "aqua",
  },
  {
    label: "Test and EOCC",
    color: "#F4CE14",
  },
  {
    label: "Training/Workshop",
    color: "#FFAEA5",
  },
  {
    label: "Training / Workshop",
    color: "#FFAEA5",
  },
  {
    label: "TRAINING/WORKSHOP",
    color: "#FFAEA5",
  },
  {
    label: "Event",
    color: "#0DCAF0",
  },
]

function Position(props) {
  const { start, end, dataWorkShop = [], isTeacherLXO, selectedTeacher } = props
  const classes = makeStyles()
  const { data: dataCountLegend, isLoading: isLoadingCountLegend } =
    useSelector((state) => state.myCalendar.callGetCountLegend)
  const auth = useSelector(selectAuth)
  const role = auth?.role
  const centerId = auth.centerId

  const getCountLegend = useMyCalendarAPIs.useGetCountLegend()
  const callGetCountLegend = useCallback(
    async (start, end) => {
      await getCountLegend({ start, end }, undefined, undefined, (error) => {
        ToastUtil.showToastError400(error)
      })
    },
    [getCountLegend]
  )

  const [triggerCountLxo, { data: dataCountLegendLXO, isFetching: isLoading }] =
    useLazyGetCountLegendLXOQuery()
  const [
    triggerGetCountLegend,
    {
      data: dataCountLegendTeacherLXO,
      isLoading: isLoadingCountLegendTeacherLXO,
    },
  ] = useLazyGetLXOCountTeachersQuery()

  useEffect(() => {
    const startFormat = moment(start).format("YYYY-MM-DD")
    const endFormat = moment(end).format("YYYY-MM-DD")

    // Teacher Calendar view (any role: LXO / LX / SALE / ...). Use the
    // teacher-specific count, but only once a center and a teacher are selected
    // so we never send teacherId=undefined (the backend rejects it).
    if (isTeacherLXO) {
      if (!centerId || !selectedTeacher?.hubUserId) return
      triggerGetCountLegend({
        centerId,
        teacherId: selectedTeacher.hubUserId,
        start: startFormat,
        end: endFormat,
      })
      return
    }

    if (role === RoleLabel[Role.LXO]) {
      if (!centerId) return
      triggerCountLxo({
        centerId,
        startDate: startFormat,
        endDate: endFormat,
      })
    } else {
      callGetCountLegend(startFormat, endFormat).then(() => {})
    }
  }, [start, end, role, centerId, isTeacherLXO, selectedTeacher])

  const groupedData = useMemo(() => {
    const isLXO = role === RoleLabel[Role.LXO]

    // The Teacher Calendar always renders the teacher-specific count, whatever
    // the viewer's role (LXO / LX / SALE / ...).
    const dataRender = isTeacherLXO
      ? dataCountLegendTeacherLXO?.data
      : isLXO
        ? dataCountLegendLXO?.data
        : dataCountLegend

    if (!Array.isArray(dataRender)) return []

    const getReportByLabel = (label) =>
      dataRender.find((item) => item.label === label)

    const dailyReport = getReportByLabel("Daily report")
    const progressReport = getReportByLabel("Progress report")
    const gradebookReport = getReportByLabel("GradeBook report")

    const reports = [
      { label: "Daily report", number: dailyReport?.number },
      { label: "Progress report", number: progressReport?.number },
      { label: "GradeBook report", number: gradebookReport?.number },
    ]

    const dataFilter = dataWorkShop.filter((item) => {
      const startDate = new Date(start)
      const endDate = new Date(end)
      const deadlineDate = new Date(item.deadline)

      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      deadlineDate.setHours(0, 0, 0, 0)

      // giữ nguyên đúng như code gốc
      return deadlineDate >= startDate && deadlineDate <= startDate
    })

    const baseItems = dataRender.filter(
      (item) =>
        item.label !== "Daily report" &&
        item.label !== "Progress report" &&
        item.label !== "GradeBook report"
    )

    const teacherReports = isTeacherLXO ? reports : []

    const otherItems = isLXO || isTeacherLXO
      ? []
      : [
          {
            label: "Report",
            number:
              (dailyReport?.number ?? 0) +
              (progressReport?.number ?? 0) +
              (gradebookReport?.number ?? 0),
          },
          {
            label: "Training/Workshop",
            number: dataFilter.length,
          },
        ]

    return [...baseItems, ...teacherReports, ...otherItems].map((item) => ({
      ...item,
      label: item.label === "Student at risk task" ? "Other Task" : item.label,
    }))
  }, [
    dataCountLegend,
    dataCountLegendLXO,
    dataCountLegendTeacherLXO,
    role,
    isTeacherLXO,
    dataWorkShop,
    start,
    end,
  ])

  return (
    <MDBox>
      <InfoIcon
        type={TooltipType.TYPE_1}
        teacherInfoIconBlock={
          teacherInfoIconBlock.BREAKDOWN_UNDER_SMALL_CALENDAR
        }
        color={TooltipIconColor.BLUE}
        className={classes.containerInfoIconWithHeading}
      >
        <MDBox>
          <MDTypography
            sx={({ breakpoints }) => ({
              ...titleTypography(breakpoints),
              lineHeight: 1.35,
              letterSpacing: "-0.8px",
              textAlign: "left",
            })}
          >
            Selected time range calendar
          </MDTypography>
        </MDBox>
      </InfoIcon>
      <MDBox
        sx={({ breakpoints }) => ({
          width: "100%",
          padding: "4px",
          marginTop: "25px",

          [breakpoints.down(1600)]: {
            marginTop: "16px",
          },
        })}
      >
        {(isTeacherLXO
          ? isLoadingCountLegendTeacherLXO
          : isLoadingCountLegend) ? (
          <MDBox>
            <Loading />
          </MDBox>
        ) : (
          groupedData.map((item, index) => {
            return (
              <Grid
                key={index}
                container
                sx={{ width: "100%", borderBottom: "1px solid #DEE2E6" }}
                alignItems="center"
                p={0.5}
              >
                <Grid xs="auto" item>
                  <MDBox
                    sx={({ breakpoints, functions: { pxToRem } }) => ({
                      width: pxToRem(24),
                      height: pxToRem(24),
                      borderRadius: pxToRem(8),
                      background:
                        timeRangeCalendar.find(
                          (_item) => _item.label === item.label
                        )?.color ?? "black",

                      [breakpoints.down(1600)]: {
                        width: pxToRem(20),
                        height: pxToRem(20),
                        borderRadius: pxToRem(7),
                      },
                    })}
                  ></MDBox>
                </Grid>
                <Grid xs item ml={2}>
                  <MDTypography
                    sx={({ breakpoints }) => ({
                      ...descriptionTypography(breakpoints),
                      lineHeight: 1.625,
                      letterSpacing: "-0.8px",
                      fontSize: "14px !important",
                    })}
                  >
                    {item.label}
                  </MDTypography>
                </Grid>
                <Grid xs="auto" item>
                  <MDTypography
                    sx={({ breakpoints }) => ({
                      ...descriptionTypography(breakpoints),
                      lineHeight: 1.25,
                      letterSpacing: "-0.8px",
                      fontSize: "14px !important",
                    })}
                  >
                    {("0" + item.number ?? 0).slice(-3)}
                  </MDTypography>
                </Grid>
              </Grid>
            )
          })
        )}
      </MDBox>
    </MDBox>
  )
}

Position.propTypes = {
  start: PropTypes.any,
  end: PropTypes.any,
}
export default Position
